import type { ReactNode } from 'react';
import { Box } from '../box/box';
import { BlockStack } from '../block-stack/block-stack';
import { InlineStack } from '../inline-stack/inline-stack';
import { Typography } from '../typography/typography';
import { Icon, type IconName } from '../icon/icon';

export interface StatCardProps {
    /** Metric name, e.g. "Total vaults". */
    label: string;
    /** Formatted metric value, e.g. "1,204". */
    value: ReactNode;
    /** Supporting line under the value, e.g. a breakdown or delta. */
    caption?: ReactNode;
    /** Optional leading glyph, shown in a tinted circle. Decorative. */
    icon?: IconName;
    testID?: string;
}

/**
 * Untitled UI "metric" stat card: a small uppercase-ish label, a large value and
 * an optional caption, with an optional tinted icon chip. Content sizes the card
 * (no fixed height) so every line scales with the OS type setting.
 */
export function StatCard({ label, value, caption, icon, testID }: StatCardProps) {
    return (
        <Box
            background="bg-card"
            borderColor="border-card"
            borderWidth="0165"
            borderRadius="300"
            padding="500"
            shadow="soft"
            flex={1}
            minWidth={200}
            testID={testID}
        >
            <InlineStack gap="400" blockAlign="start" wrap={false}>
                <BlockStack gap="200" flex={1}>
                    <Typography variant="body-sm" fontWeight="medium" tone="text-secondary">
                        {label}
                    </Typography>
                    <Typography variant="heading-2xl" as="p" fontWeight="semibold">
                        {value}
                    </Typography>
                    {caption ? (
                        <Typography variant="body-sm" tone="text-secondary">
                            {caption}
                        </Typography>
                    ) : null}
                </BlockStack>
                {icon ? (
                    <Box
                        background="primary-subdued"
                        borderRadius="full"
                        padding="300"
                        minWidth={44}
                        minHeight={44}
                    >
                        <Icon name={icon} tone="text-primary" size={22} />
                    </Box>
                ) : null}
            </InlineStack>
        </Box>
    );
}
